import {
  ElectricBoltOutlined,
  SendOutlined,
  CheckCircleOutlineOutlined,
  AccountBalanceWalletOutlined,
} from '@mui/icons-material';
import { Button, CardContainer } from 'finara-components';
import { useNavigate } from 'react-router-dom';

import { useAccounts } from '@/hooks/queries/useAccounts';
import { useAuth } from '@/hooks/useAuth';
import { formatNumber } from '@/utils/formatters';

interface DashboardHeaderProps {
  title?: string;
  subtitle?: string;
  showRealizarPago?: boolean;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  title = 'Panel Administrativo',
  subtitle = 'Gestiona pagos, operaciones y clientes desde un solo lugar',
  showRealizarPago = true,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Fetch organization accounts for the super admin's own organization
  const { data: accountsData, isLoading: isLoadingAccounts } = useAccounts({
    type: 'organization',
    status: 'active',
    limit: 1,
    organization_id: user?.organization?.id,
  });

  // Get the main organization account (first one)
  const mainAccount = accountsData?.data[0];

  const handleRealizarPago = () => {
    void navigate('/pagos');
  };

  // Format balance
  const formatBalance = (value: number) => {
    return `$${formatNumber(value)} MXN`;
  };
  return (
    <CardContainer className="mt-[21px] text-white mb-8" style={{ background: 'linear-gradient(135deg, #1e8a7a 0%, #175757 50%, #103949 100%)' }}>
      <div className="text-center py-2 md:px-7">
        <h1 className="text-[31.5px] font-bold mb-2">{title}</h1>
        <p className="text-[17.5px] text-white/90 mb-[21px]">{subtitle}</p>

        {showRealizarPago && (
          <div className="mb-[21px]">
            <Button
              variant="white"
              size="large"
              startIcon={<ElectricBoltOutlined fontSize="inherit" />}
              endIcon={<SendOutlined fontSize="inherit" />}
              onClick={handleRealizarPago}
            >
              Realizar Pago
            </Button>
          </div>
        )}
        <div className="flex flex-wrap justify-center max-sm:gap-3 md:gap-9 text-[12.25px] text-white/80">
          <div className="flex items-center space-x-2">
            <CheckCircleOutlineOutlined fontSize="small" />
            <span>Pagos singulares y masivos</span>
          </div>
          <div className="flex items-center space-x-2">
            <CheckCircleOutlineOutlined fontSize="small" />
            <span>Autorización OTP segura</span>
          </div>
          <div className="flex items-center space-x-2">
            <CheckCircleOutlineOutlined fontSize="small" />
            <span>Ejecución inmediata o programada</span>
          </div>
        </div>
        {/* Main Account Section */}
        {(isLoadingAccounts || mainAccount) && (
          <div className="mt-6 flex justify-center">
            <div className="bg-white/15 border border-white/30 backdrop-blur-sm rounded-lg px-6 py-4 inline-flex items-center gap-3">
              {isLoadingAccounts ? (
                <>
                  <AccountBalanceWalletOutlined fontSize="large" className="text-white" />
                  <div className="text-left">
                    <p className="text-xs text-white/70 uppercase tracking-wide">Cuenta Principal</p>
                    <p className="text-2xl font-bold">Cargando...</p>
                  </div>
                </>
              ) : mainAccount ? (
                <>
                  <AccountBalanceWalletOutlined fontSize="large" className="text-white" />
                  <div className="text-left">
                    <p className="text-xs text-white/70 uppercase tracking-wide">Cuenta Principal</p>
                    <p className="text-2xl font-bold">{formatBalance(mainAccount.balance)}</p>
                    <p className="text-xs text-white/60 font-mono mt-1">
                      CLABE: {mainAccount.clabe}
                    </p>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </CardContainer>
  );
};
